import { getTranslation, Lang, Translation } from "@/data/translation";
import { PortfolioHoldingsData } from "@/services/13f/portfolio-distribution";
import { ChartData } from "chart.js";

export const extractHoldingRatioData = ({
  lang,
  data,
}: {
  lang: Lang;
  data: PortfolioHoldingsData<"holding-ratio">[];
}): ChartData<"bar"> | null => {
  if (data.length === 0) return null;

  const { t } = getTranslation({ lang });
  const { t: en } = getTranslation({ lang: "en" });
  const labels = [...new Set(data.map((item) => item.quarter))];
  const symbols = [
    ...new Set(data.map((item) => item.symbol ?? t.chip.noStock)),
  ];
  const colorsMap = getHoldingsColorsMap({ t: en, symbols });

  const datasets = symbols.map((symbol) => ({
    // since the symbol is in English, we need to translate it to Chinese
    label:
      Object.values(t.chip)[
        Object.entries(en.chip).findIndex((entry) => entry[1] === symbol)
      ] || symbol,
    backgroundColor: colorsMap.get(symbol),
    borderColor: colorsMap.get(symbol),
    data: labels.map((quarter) => {
      const dataPoint = data.find(
        (item) => item.quarter === quarter && item.symbol === symbol
      );
      return dataPoint?.proportion ? dataPoint.proportion * 0.01 : 0;
    }),
  }));

  return {
    labels,
    datasets,
  };
};

function getHoldingsColorsMap({
  t,
  symbols,
}: {
  t: Translation;
  symbols: string[];
}) {
  const sourceColorsMap = new Map<string | number, string>([
    [0, "#2D80AFCC"],
    [1, "#003D70CC"],
    [2, "#FDB62BCC"],
    [3, "#F67F13CC"],
    [4, "#EA4335CC"],
    [5, "#1f77b4"],
    [6, "#ff7f0e"],
    [7, "#2ca02c"],
    [8, "#d62728"],
    [9, "#9467bd"],
    [10, "#8c564b"],
    [11, "#e377c2"],
    [12, "#7f7f7f"],
    [13, "#bcbd22"],
    [14, "#17becf"],
    [15, "#aec7e8"],
    [16, "#ffbb78"],
    [17, "#98df8a"],
    [18, "#ff9896"],
    [19, "#c5b0d5"],
    [20, "#c49c94"],
    [t.chip.others, "#58C1D7CC"],
    [t.chip.noStock, "#DEDEDE"],
  ]);
  let index = 0;
  const colorsMap = new Map<string | number, string>();
  symbols.forEach((symbol) => {
    if (sourceColorsMap.has(symbol)) {
      colorsMap.set(symbol, sourceColorsMap.get(symbol) ?? "");
    } else {
      colorsMap.set(symbol, sourceColorsMap.get(index++) ?? "");
    }
  });
  return colorsMap;
}
